/**
 * Extracts unique colors from SVG content
 * @param {string} svgContent - The SVG content as a string
 * @returns {Array<{value: string, type: string}>} Array of unique colors with their types
 */
export function extractColors(svgContent) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgContent, 'image/svg+xml');
  const colors = new Set();

  // Function to process color values
  const processColorValue = (value) => {
    if (!value || value === 'none' || value === 'transparent') return;
    if (value.startsWith('url(')) {
      colors.add(JSON.stringify({ value: 'gradient', type: 'gradient' }));
      return;
    }
    // Handle rgb/rgba values
    if (value.startsWith('rgb')) {
      const rgb = value.match(/\d+/g);
      if (rgb) {
        const hex = rgbToHex(parseInt(rgb[0]), parseInt(rgb[1]), parseInt(rgb[2]));
        colors.add(JSON.stringify({ value: hex, type: 'solid' }));
      }
    } else if (value.startsWith('#')) {
      colors.add(JSON.stringify({ value: value.toLowerCase(), type: 'solid' }));
    } else {
      // Could be a CSS variable or class
      colors.add(JSON.stringify({ value: 'css', type: 'css' }));
    }
  };

  // Process fill and stroke attributes
  const elements = doc.getElementsByTagName('*');
  for (const element of elements) {
    const fill = element.getAttribute('fill');
    const stroke = element.getAttribute('stroke');
    if (fill) processColorValue(fill);
    if (stroke) processColorValue(stroke);
  }

  return Array.from(colors).map(str => JSON.parse(str));
}

/**
 * Updates a specific color attribute (fill or stroke) for a specific element by ID
 * @param {string} svgContent - Original SVG content
 * @param {string} elementId - The element's id or data-svgtoolbox-id
 * @param {string} colorType - 'fill' or 'stroke'
 * @param {string} newColor - New color value
 * @returns {string} Updated SVG content
 */
export function updateSvgColors(svgContent, elementId, colorType, newColor) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgContent, 'image/svg+xml');
  const elements = doc.getElementsByTagName('*');
  for (const element of elements) {
    const id = element.getAttribute('data-svgtoolbox-id') || element.getAttribute('id');
    if (id === elementId) {
      element.setAttribute(colorType, newColor);
      break; // Only update the first match
    }
  }
  return new XMLSerializer().serializeToString(doc);
}

/**
 * Converts RGB values to hex color
 * @param {number} r - Red value (0-255)
 * @param {number} g - Green value (0-255)
 * @param {number} b - Blue value (0-255)
 * @returns {string} Hex color code
 */
function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
}

// Flatten nested groups and extract all elements
const flattenSvg = (node, parentId = null) => {
  if (!node) return [];
  let elements = [];
  const nodeId = node.attributes?.id || `${node.name}-${Math.random().toString(36).substring(2, 11)}`;
  // Assign a unique data-svgtoolbox-id if not present
  if (!node.attributes) {
    node.attributes = {};
  }
  if (!node.attributes['data-svgtoolbox-id']) {
    node.attributes['data-svgtoolbox-id'] = nodeId;
  }
  if (node.name === 'g') {
    if (node.children) {
      node.children.forEach(child => {
        elements = elements.concat(flattenSvg(child, nodeId));
      });
    }
  } else {
    elements.push({
      ...node,
      attributes: {
        ...node.attributes,
        'data-svgtoolbox-id': node.attributes['data-svgtoolbox-id'] || nodeId
      }
    });
  }
  return elements;
};

// Utility to parse style strings to React style objects
export function parseStyleString(styleString) {
  if (!styleString) return {};
  return styleString.split(';').reduce((acc, rule) => {
    const [key, value] = rule.split(':').map(s => s && s.trim());
    if (key && value) acc[key.replace(/-([a-z])/g, g => g[1].toUpperCase())] = value;
    return acc;
  }, {});
}

// Utility to validate hex color values
export function isValidHexColor(value) {
  return typeof value === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value);
}

// Centralized list of SVG shape elements
export const SVG_SHAPES = ['path', 'rect', 'circle', 'ellipse', 'polygon', 'polyline', 'line', 'text']; 